import { ReactNode, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useSearchParams } from 'react-router'
import { useDebounce } from 'react-use'
import { useCanBlockNumber } from '~/atoms/BlockedNumbersState'
import { useModalState } from '~/atoms/ModalState'
import {
  CopyLinkToClipboard,
  Delete,
  MarkAsUnread,
  NewCall,
  NewText,
} from '~/components/compositional/QuickActions'
import { FontStyle } from '~/components/core'
import {
  ResponsivePage,
  useTogglePanel,
} from '~/components/layout/ResponsivePage'
import { Loading } from '~/components/Loading'
import { ResourceFailedToLoadNotice } from '~/components/ResourceFailedToLoadNotice'
import { EmptyDetailView } from '~/components/shared/EmptyDetailView'
import { ToggleBlockedNumberButton } from '~/components/specialized/BlockedNumber/ToggleBlockedNumberButton'
import { VoicemailOperation } from '~/hooks/toast/eventHandlers/voicemailToastEventHandler'
import { useToastAlert } from '~/hooks/toast/useToastAlerts'
import { useAnalytics } from '~/hooks/useAnalytics'
import { useClipboard } from '~/hooks/useClipboard'
import { ApplicationRoutes } from '~/hooks/useRouting'
import { MoveVoicemail } from '~/pages/Voicemail/MoveVoicemail'
import { MoveVoicemailModal } from '~/pages/Voicemail/MoveVoicemailModal'
import {
  VoicemailDetailHeader,
  VoicemailDetailView,
} from '~/pages/Voicemail/VoicemailDetailView'
import { VoicemailListView } from '~/pages/Voicemail/VoicemailListView'
import emitterServiceInstance from '~/services/EventEmitter/EmitterService'
import {
  NotificationDisposition,
  NotificationDomain,
  NotificationEmitterEventType,
  NotificationPayload,
} from '~/services/EventEmitter/types/NotificationEmitterTypes'
import { AnalyticsNamespace } from '~/types/Analytics'
import { VoicemailAnalyticsEvents } from '~/types/Analytics/VoicemailAnalyticsEvents'
import { UnreadVoicemailStatusFilter } from '~/types/ApiRequestTypes'
import { SearchValue } from '~/types/Shared'
import { formatMilliseconds, getValidNumber } from '~/utils/formatter'
import { useVoicemailViewModel } from '~/viewModels/Voicemail/VoicemailViewModel'

import { Voicemail } from '@mangovoice/types'

export const VoicemailPage = () => {
  const analytics = useAnalytics(AnalyticsNamespace.VOICEMAIL)
  const { voicemailViewModel, actions } = useVoicemailViewModel()
  const navigate = useNavigate()
  const [debounceSearch, setDebounceSearch] = useState(SearchValue.empty)
  const [isSearchFocused, setIsSearchFocused] = useState<boolean>()
  const [playerPosition, setPlayerPosition] = useState<number>()
  const { copyToClipboard } = useClipboard()
  const { emit } = emitterServiceInstance
  const { toastError } = useToastAlert()
  const [searchParams] = useSearchParams()
  const { t } = useTranslation()
  const playbackTimestampQueryParam = useMemo(() => {
    return searchParams.get('t') ? Number(searchParams.get('t')) : undefined
  }, [searchParams])
  const handleMoveVoicemailSuccess = (
    voicemailId: number,
    newExtensionId: number,
    markAsUnread: boolean,
  ) => {
    const payload: NotificationPayload<NotificationDomain.VOICEMAIL> = {
      disposition: NotificationDisposition.Success,
      domain: NotificationDomain.VOICEMAIL,
      operation: VoicemailOperation.MoveVoicemail,
      message: markAsUnread
        ? t('voicemail.moveSuccess.markAsUnread')
        : t('voicemail.moveSuccess.markAsRead'),
      context: {
        voicemailId,
        newExtensionId,
        markAsUnread,
      },
    }

    emit(NotificationEmitterEventType.PROCESS_UPDATED, payload)
  }

  const handleMoveVoicemailError = (
    voicemailId: number,
    newExtensionId: number,
    markAsUnread: boolean,
  ) => {
    const payload: NotificationPayload<NotificationDomain.VOICEMAIL> = {
      disposition: NotificationDisposition.Error,
      domain: NotificationDomain.VOICEMAIL,
      operation: VoicemailOperation.MoveVoicemail,
      message: t('voicemail.moveError'),
      context: {
        voicemailId,
        newExtensionId,
        markAsUnread,
      },
    }

    emit(NotificationEmitterEventType.PROCESS_UPDATED, payload)
  }

  const {
    messagingModal: [, setChatModalState],
    callModal: [, setCallModalState],
    confirmModal: [, setConfirmModalState],
    isAnyModalOpen,
  } = useModalState()

  const {
    state: { isOpen: isListPanelOpen },
    togglePanelVisibility,
  } = useTogglePanel()
  // This is wrapped in a useEffect because the voicemail selection happens over multiple renders,
  // which causes the panel to open again after the selection is made.
  useEffect(() => {
    if (!voicemailViewModel.selectedVoicemail && !isListPanelOpen) {
      togglePanelVisibility(true)
    }
  }, [])

  const isInternal =
    (voicemailViewModel.selectedVoicemail?.caller_id_number.length ?? 0) < 5

  const canBlockNumber = useCanBlockNumber(
    voicemailViewModel.selectedVoicemail?.from_phone_number.phone_number,
  )

  useDebounce(
    () => {
      actions.appendVoicemailViewModelState({ searchValue: debounceSearch })
    },
    350,
    [debounceSearch],
  )

  //#region Callback Handlers
  const handleOnSearchFocusedChanged = (focused: boolean) => {
    setIsSearchFocused(focused)
  }

  const handleVoicemailSelected = (selectedVoicemail: Voicemail) => {
    analytics.track(VoicemailAnalyticsEvents.VoicemailSelected)
    navigate(`${ApplicationRoutes.VOICEMAIL}/${selectedVoicemail.id}`)
    togglePanelVisibility(false)
  }

  const handleVoicemailUnselected = () => {
    navigate(ApplicationRoutes.VOICEMAIL)
  }

  const handleExtensionSelected = (selectedExtension: string | number) => {
    analytics.track(VoicemailAnalyticsEvents.ExtensionFilterChanged)
    actions.appendVoicemailViewModelState({
      selectedExtensionId: selectedExtension,
    })
  }

  const handleVoicemailDeleteRequested = (voicemailId: number) => {
    analytics.track(VoicemailAnalyticsEvents.VoicemailDeleted)
    actions.deleteVoicemail(voicemailId)
    navigate(ApplicationRoutes.VOICEMAIL)
  }

  const handleVoicemailMarkedUnreadRequested = (voicemailId: number) => {
    analytics.track(VoicemailAnalyticsEvents.VoicemailMarkedAsUnread)
    actions.markVoicemailUnread(voicemailId)
  }

  const handleSearchChanged = (newSearchValue: SearchValue) => {
    analytics.track(VoicemailAnalyticsEvents.SearchTermChanged)
    setDebounceSearch(newSearchValue)
  }

  const handleUnreadFilterChanged = (
    unreadFilter: UnreadVoicemailStatusFilter,
  ) => {
    analytics.track(VoicemailAnalyticsEvents.UnreadFilterChanged)
    actions.appendVoicemailViewModelState({ unreadFilter })
  }

  const handleNextPageRequested = () => {
    actions.fetchNextVoicemailPage()
  }

  const handleCopyToClipboard = (timestamp?: number) => {
    analytics.track(
      timestamp
        ? VoicemailAnalyticsEvents.LinkCopiedAtTimestamp
        : VoicemailAnalyticsEvents.LinkCopied,
    )
    const url =
      typeof timestamp !== 'undefined'
        ? `${window.location.origin}${ApplicationRoutes.VOICEMAIL}/${voicemailViewModel.selectedVoicemailId}?t=${timestamp ? Math.floor(timestamp) : undefined}`
        : `${window.location.origin}${ApplicationRoutes.VOICEMAIL}/${voicemailViewModel.selectedVoicemailId}`
    copyToClipboard(
      url,
      () => console.log('success'),
      () => toastError(t('voicemail.copyLinkError')),
    )
  }
  const handleNewCall = () => {
    window.gtag('event', 'quick_action_c2c_from_voicemail', {})
    const number = getValidNumber(
      voicemailViewModel.selectedVoicemail?.caller_id_number,
    )
    setCallModalState({ open: true, callee: number })
  }
  const handleNewText = () => {
    window.gtag('event', 'quick_action_text_from_voicemail', {})
    const number = getValidNumber(
      voicemailViewModel.selectedVoicemail?.caller_id_number,
    )
    setChatModalState({ open: true, number })
  }
  const handleDelete = () => {
    setConfirmModalState({
      title: t('deleteConfirmModal.title'),
      message: t('deleteConfirmModal.message'),
      confirmText: t('deleteConfirmModal.deleteButton'),
      cancelText: t('deleteConfirmModal.cancelButton'),
      closeAfterConfirm: true,
      isDestructive: true,
      onConfirm: () => {
        if (!voicemailViewModel.selectedVoicemail?.id) {
          return
        }

        handleVoicemailDeleteRequested(voicemailViewModel.selectedVoicemail.id)
      },
    })
  }

  const [moveVoicemailModalOpen, setMoveVoicemailModalOpen] = useState(false)

  const handleMoveVoicemail = () => {
    setMoveVoicemailModalOpen(true)
  }

  const handleMoveVoicemailConfirmed = async (
    selectedExtensionId: number,
    markAsUnread: boolean,
  ) => {
    if (!voicemailViewModel.selectedVoicemail?.id) return
    const voicemailId = voicemailViewModel.selectedVoicemail.id

    setMoveVoicemailModalOpen(false)

    try {
      await actions.moveVoicemailAsync({
        voicemailId,
        extensionId: selectedExtensionId,
      })

      if (markAsUnread) {
        // Mark the voicemail as unread in the target extension
        await actions.markVoicemailUnreadAsync(voicemailId)
        actions.updateLocalUnreadStatus(voicemailId, false, selectedExtensionId)
      }

      // Update the voicemail's extension_id and handle list update
      actions.updateVoicemailExtensionId(voicemailId, selectedExtensionId)

      handleVoicemailUnselected()

      handleMoveVoicemailSuccess(voicemailId, selectedExtensionId, markAsUnread)
    } catch (error) {
      console.error('Error during move operation:', error)
      handleMoveVoicemailError(voicemailId, selectedExtensionId, markAsUnread)
    }
  }

  const totalVoicemailExtensions =
    voicemailViewModel.voicemailExtensionsState?.length ?? 0

  const handleAudioPlayerPositionChange = (position: number) => {
    setPlayerPosition(position)
  }
  //#endregion

  //#region useEffects
  //on mount, fetch voicemail list from origin and if extensions have not yet been pulled, pull them.
  useEffect(() => {
    //on mount fetch voicemail extensions
    actions.fetchVoicemailExtensions()

    // Fetch all extensions for the modal
    actions.fetchAllExtensions()

    //on mount, fetch voicemail list from origin
    actions.fetchVoicemailsFromOrigin()

    //on unmount, reset all selection state
    return () => {
      actions.resetAllSelectionState()
    }
  }, [])

  //watches for filter changes and fetches voicemails from origin
  useEffect(() => {
    actions.fetchVoicemailsFromOrigin()
  }, [
    voicemailViewModel.unreadFilter,
    voicemailViewModel.searchValue,
    voicemailViewModel.selectedExtensionId,
  ])

  //watches for changes to selected voicemail, and marks it as read if it's not already
  useEffect(() => {
    //if a selected voicemail is not loaded, or has been read, do nothing
    if (
      !voicemailViewModel.selectedVoicemail ||
      voicemailViewModel.selectedVoicemail.read_at
    ) {
      return
    }

    actions.markVoicemailRead(voicemailViewModel.selectedVoicemail.id)
  }, [voicemailViewModel.selectedVoicemail])
  //#endregion

  const getListViewContent = () => {
    let content: ReactNode

    const isListViewLoading =
      voicemailViewModel.voicemailListLoading ||
      voicemailViewModel.voicemailListFetching

    if (!content) {
      content = (
        <VoicemailListView
          voicemailList={voicemailViewModel.voicemailListState?.voicemails}
          listDataLoading={isListViewLoading}
          selectedVoicemail={voicemailViewModel.selectedVoicemail}
          selectedVoicemailId={voicemailViewModel.selectedVoicemailId}
          voicemailExtensions={voicemailViewModel.voicemailExtensionsState}
          unreadFilter={voicemailViewModel.unreadFilter}
          searchValue={voicemailViewModel.searchValue}
          selectedExtension={voicemailViewModel.selectedExtensionId}
          onNextPageRequested={handleNextPageRequested}
          onVoicemailSelected={handleVoicemailSelected}
          onExtensionSelected={handleExtensionSelected}
          onUnreadFilterChanged={handleUnreadFilterChanged}
          onSearchFocusedChanged={handleOnSearchFocusedChanged}
          onSearchChanged={handleSearchChanged}
        />
      )
    }

    return { content }
  }

  const getDetailViewContent = () => {
    let detailViewContent: ReactNode
    let detailHeaderContent: ReactNode
    if (
      voicemailViewModel.selectedVoicemailLoading ||
      voicemailViewModel.selectedVoicemailFetching
    ) {
      detailHeaderContent = null
      detailViewContent = (
        <Loading
          fontStyle={FontStyle.Normal}
          text={t('voicemail.loadingText')}
        />
      )
    }

    if (
      voicemailViewModel.getSelectedVoicemailError &&
      !voicemailViewModel.selectedVoicemailNotFound
    ) {
      detailHeaderContent = null
      detailViewContent = (
        <ResourceFailedToLoadNotice
          error={voicemailViewModel.getSelectedVoicemailError}
        />
      )
    }

    if (!detailViewContent) {
      if (voicemailViewModel.selectedVoicemail === undefined) {
        detailHeaderContent = null
        detailViewContent = (
          <EmptyDetailView message={t('voicemail.emptyDetailViewMessage')} />
        )
      } else {
        detailHeaderContent = (
          <VoicemailDetailHeader
            caller_id_name={voicemailViewModel.selectedVoicemail.caller_id_name}
            caller_id_number={
              voicemailViewModel.selectedVoicemail.caller_id_number
            }
            selectedVoicemailBoxName={
              voicemailViewModel.selectedVoicemailBoxName
            }
          />
        )
        detailViewContent = (
          <VoicemailDetailView
            selectedVoicemail={voicemailViewModel.selectedVoicemail}
            audioTimestamp={playbackTimestampQueryParam}
            forceDisableAudioPlayerKeyboardInput={isSearchFocused}
            isAnyModalOpen={isAnyModalOpen}
            onAudioPlayerPositionChange={handleAudioPlayerPositionChange}
          />
        )
      }
    }
    return { detailViewContent, detailHeaderContent }
  }

  const getQuickActionButtons = () => {
    const quickActionButtons: JSX.Element[] = []
    quickActionButtons.push(
      <CopyLinkToClipboard
        key="link"
        timeStamp={formatMilliseconds(playerPosition)}
        onCopyLink={() => handleCopyToClipboard()}
        onCopyLinkAtTimestamp={() => handleCopyToClipboard(playerPosition)}
      />,
    )
    if (!isInternal) {
      quickActionButtons.push(
        <NewCall key="call" onClick={handleNewCall} />,
        <NewText key="text" onClick={handleNewText} />,
      )
    }
    if (voicemailViewModel.selectedVoicemail) {
      quickActionButtons.push(
        <MarkAsUnread
          action="mark_unread"
          onClick={() =>
            handleVoicemailMarkedUnreadRequested(
              voicemailViewModel.selectedVoicemail?.id ?? 0,
            )
          }
        />,
      )
    }
    if (totalVoicemailExtensions >= 2) {
      quickActionButtons.push(
        <MoveVoicemail key="move" onClick={handleMoveVoicemail} />,
      )
    }
    if (canBlockNumber) {
      quickActionButtons.push(
        <ToggleBlockedNumberButton
          key="toggle-blocked"
          phoneNumber={
            voicemailViewModel.selectedVoicemail?.from_phone_number.phone_number
          }
        />,
      )
    }
    quickActionButtons.push(<Delete key="delete" onClick={handleDelete} />)
    return quickActionButtons
  }

  const getListAndDetailViewContent = () => {
    const listViewContent = getListViewContent()
    const { detailViewContent, detailHeaderContent } = getDetailViewContent()
    const quickActionButtons = getQuickActionButtons()

    return {
      listView: { listViewContent },
      headerView: { detailHeaderContent },
      detailView: { detailViewContent },
      quickActionButtons,
    }
  }

  const listAndDetailViewContent = useMemo(getListAndDetailViewContent, [
    voicemailViewModel,
  ])

  return (
    <>
      <ResponsivePage
        bg="gray.300"
        listPanel={listAndDetailViewContent.listView.listViewContent.content}
        header={listAndDetailViewContent.headerView.detailHeaderContent}
        detailPanel={listAndDetailViewContent.detailView.detailViewContent}
        quickActionButtons={listAndDetailViewContent.quickActionButtons}
      />

      {moveVoicemailModalOpen && (
        <MoveVoicemailModal
          isOpen={moveVoicemailModalOpen}
          onClose={() => setMoveVoicemailModalOpen(false)}
          voicemailBoxes={voicemailViewModel.allExtensionsState ?? []}
          onMove={handleMoveVoicemailConfirmed}
          currentExtensionId={
            voicemailViewModel.selectedVoicemail?.extension_id ?? 0
          }
        />
      )}
    </>
  )
}
